import { useState } from 'react';
import './Home.scss';
import { Avatar } from '@appkit4/react-components/avatar';
import useLocale from '../locale/useLocale';
import useTitle from '../helpers/useTitle';
import ActionCard from '../components/shared/actionCard/ActionCard';
import ListingTable from '../components/shared/listingTable/ListingTable';
import Illustration1 from '../assets/illustrations/Illustration_1.png';
import Illustration2 from '../assets/illustrations/Illustration_2.png';
import Illustration3 from '../assets/illustrations/Ilustration_3.png';
import { useGetV1ProcessGetRecentlyAccessedProcesses } from '../api/generated/endpoint';
import { RecentProcessAccessResponse } from '../api/generated/models';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../Constants';
import { useTenantProcessTypes } from '../helpers/useTenantProcessTypes';

interface Owner {
    name: string;
    avatar: string;
}

interface TableRow {
    processId?: number;
    engagementName: string;
    location: string;
    documentType: string[];
    owner: Owner;
    lastUpdated: string;
    engagementType: string;
    validationStatus: string;
    processTypeId?: number;
}

export const Home = () => {
    const { l } = useLocale();
    const navigate = useNavigate();
    useTitle(l('_home'));
    const { enabledSet, loading: processTypesLoading } =
        useTenantProcessTypes();

    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState(0);
    const pageSize = 5;

    const { data: recentProcesses, isLoading } =
        useGetV1ProcessGetRecentlyAccessedProcesses({
            skip: 0,
            take: 10,
        });

    const handleActionCardClick = (cardType: number) => {
        const path = `${AppRoutes.CreateProcess}`;
        navigate(path, {
            state: {
                folderId: -1,
                folderName: '',
                processType: cardType,
            },
        });
    };

    const actionCards = [
        {
            imageSrc: Illustration1,
            title: 'Extract from PDF',
            description:
                'Helps you to extract and summarize the information from the pdf by uploading in just a single click',
            type: 1,
            onClick: () => handleActionCardClick(1),
        },
        {
            imageSrc: Illustration2,
            title: 'Fill in PDF',
            description:
                'Helps you to fill in the details in the pdf that has been uploaded to process further with extract',
            type: 2,
            onClick: () => handleActionCardClick(2),
        },
        {
            imageSrc: Illustration3,
            title: 'Fill in online form',
            description:
                'Helps you to fill in the predefined details of the form that has already been uploaded',
            type: 3,
            onClick: () => handleActionCardClick(3),
        },
    ];

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const transformApiData = (
        apiData: RecentProcessAccessResponse[]
    ): TableRow[] => {
        return apiData.map((process) => ({
            processId: process.processId || 0,
            engagementName: process.processName || '',
            location: process.folderName || '',
            documentType: process.documentTypes || [],
            owner: {
                name: process.createdByUserName || '',
                avatar: process.createdByUserName
                    ? process.createdByUserName
                          .split(' ')
                          .map((name) => name.charAt(0))
                          .join('')
                    : '',
            },
            lastUpdated: formatDate(process.lastUpdatedDate || ''),
            engagementType: process.processTypeName || '',
            validationStatus: getStatusString(process.processStatus),
            processTypeId: process.processTypeId,
        }));
    };

    const getStatusString = (status: number | undefined): string => {
        switch (status) {
            case 0:
                return 'Awaiting Documentation';
            case 1:
                return 'Processing';
            case 2:
                return 'Action Required';
            case 3:
                return 'Completed';
            default:
                return 'Unknown';
        }
    };

    const handleTabChange = (index: number) => {
        setActiveTab(index);
        setCurrentPage(1);
    };

    const getFilteredData = (
        data: TableRow[],
        activeTabIndex: number
    ): TableRow[] => {
        if (activeTabIndex === 0) {
            return data;
        }

        const tabToProcessTypeMap: { [key: number]: number } = {
            1: 1,
            2: 2,
            3: 3,
        };

        const targetProcessTypeId = tabToProcessTypeMap[activeTabIndex];
        if (targetProcessTypeId) {
            return data.filter(
                (item) => item.processTypeId === targetProcessTypeId
            );
        }

        return data;
    };

    const columns = [
        {
            field: 'engagementName',
            header: 'Engagement name',
            renderCell: (row: TableRow) => (
                <span className="fw-bold">{row.engagementName}</span>
            ),
        },
        { field: 'location', header: 'Location' },
        {
            field: 'documentType',
            header: 'Document type',
            renderCell: (row: TableRow) => {
                const docs = row.documentType;
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span>{docs[0]}</span>
                        {docs.length > 1 && (
                            <span className="document-count">
                                +{docs.length - 1}
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'owner',
            header: 'Owner',
            renderCell: (row: any) => (
                <div className="d-flex align-items-center gap-2">
                    <Avatar label={row.owner.avatar} />
                    <span>{row.owner.name}</span>
                </div>
            ),
        },
        {
            field: 'lastUpdated',
            header: 'Last Updated',
            renderCell: (row: TableRow) => <span>{row.lastUpdated}</span>,
        },
        {
            field: 'engagementType',
            header: 'Engagement type',
            renderCell: (row: TableRow) => {
                const typeClass = row.engagementType
                    .toLowerCase()
                    .replace(' ', '-');
                return (
                    <span className={`engagement-type ${typeClass}`}>
                        {row.engagementType}
                    </span>
                );
            },
        },
        {
            field: 'validationStatus',
            header: 'Validation status',
            renderCell: (row: TableRow) => {
                const typeClass = row.validationStatus
                    .toLowerCase()
                    .replace(' ', '-');
                return (
                    <span className={`validation-status ${typeClass}`}>
                        {row.validationStatus}
                    </span>
                );
            },
        },
    ];

    const tabs = [
        { label: 'All', value: 'all', icon: 'icon-document-multiple-outline' },
        {
            label: 'Classified extracted documents',
            value: 'extracted',
            icon: 'icon-document-file-outline',
        },
        {
            label: 'Fill PDF documents',
            value: 'fillPdf',
            icon: 'icon-pdf-outline',
        },
        {
            label: 'Fill in online form documents',
            value: 'fillForm',
            icon: 'icon-document-file-outline',
        },
    ];

    const handleRowClick = (event: MouseEvent, index: number, data: any) => {
        navigate(`/process/${data.processId}`);
    };
    const transformedData = recentProcesses
        ? transformApiData(recentProcesses.items || [])
        : [];
    const filteredData = getFilteredData(transformedData, activeTab);

    return (
        <div className="home-container">
            <div className="action-cards">
                {!processTypesLoading &&
                    actionCards.map((card, index) => (
                        <ActionCard
                            key={index}
                            {...card}
                            onClick={card.onClick}
                            disabled={
                                processTypesLoading ||
                                !enabledSet.has(card.type)
                            }
                        />
                    ))}
            </div>
            <div className="table-container-home">
                <ListingTable
                    title="Recent activity"
                    tabs={tabs}
                    activeTab={activeTab} // Pass active tab state
                    onTabChange={handleTabChange} // Pass tab change handler
                    columns={columns}
                    data={filteredData} // Use filtered data instead of raw data
                    totalCount={filteredData.length} // Use filtered data count
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={setCurrentPage}
                    loading={isLoading}
                    onRowClick={handleRowClick}
                />
            </div>
        </div>
    );
};

export default Home;
